<script setup>
import { reactive, ref } from 'vue';
import Button from 'next/button/Button.vue';
import BugReportRecording from './BugReportRecording.vue';
import BugReportAttachments from './BugReportAttachments.vue';
defineProps({ busy: { type: Boolean, default: false } });
const emit = defineEmits(['submit', 'recording']);
const files = ref([]);
const captureBusy = ref(false);
const fields = reactive({
  diagnostic_token: '',
  submission_id: crypto.randomUUID(),
  kind: 'bug',
  title: '',
  description: '',
  impact: 'medium',
  steps: '',
  expected_result: '',
  observed_result: '',
});
const submit = () => {
  if (!captureBusy.value) emit('submit', { ...fields }, files.value);
};
</script>

<template>
  <form class="space-y-4" @submit.prevent="submit">
    <fieldset :disabled="busy" class="space-y-4">
      <label class="block">
        <span>{{ $t('BUG_REPORTS.fields.kind') }}</span>
        <select v-model="fields.kind" class="w-full">
          <option
            v-for="kind in ['bug', 'improvement', 'idea']"
            :key="kind"
            :value="kind"
          >
            {{ $t(`BUG_REPORTS.values.${kind}`) }}
          </option>
        </select>
      </label>
      <label class="block">
        <span>{{ $t('BUG_REPORTS.fields.title') }}</span>
        <input
          v-model="fields.title"
          type="text"
          required
          minlength="5"
          maxlength="160"
          class="w-full"
      /></label>
      <label class="block">
        <span>{{ $t('BUG_REPORTS.fields.description') }}</span>
        <textarea
          v-model="fields.description"
          required
          minlength="20"
          maxlength="10000"
          rows="5"
          class="w-full"
        />
      </label>
      <template v-if="fields.kind === 'bug'">
        <label class="block">
          <span>{{ $t('BUG_REPORTS.fields.impact') }}</span>
          <select v-model="fields.impact" required class="w-full">
            <option
              v-for="impact in ['low', 'medium', 'high', 'urgent']"
              :key="impact"
              :value="impact"
            >
              {{ $t(`BUG_REPORTS.values.${impact}`) }}
            </option>
          </select>
        </label>
        <details class="space-y-3">
          <summary class="cursor-pointer text-sm text-n-slate-11">
            {{ $t('BUG_REPORTS.additional_details') }}
          </summary>
          <label
            v-for="field in ['steps', 'expected_result', 'observed_result']"
            :key="field"
            class="block"
            >{{ $t(`BUG_REPORTS.fields.${field}`)
            }}<textarea
              v-model="fields[field]"
              maxlength="10000"
              class="w-full"
            />
          </label>
        </details>
      </template>
      <BugReportRecording
        @update:model-value="fields.diagnostic_token = $event"
        @recording="emit('recording', $event)"
        @busy="captureBusy = $event"
      />
      <BugReportAttachments v-model="files" />
      <p class="text-sm text-n-slate-11">{{ $t('BUG_REPORTS.notice') }}</p>
      <Button
        class="!text-n-slate-12 dark:!text-n-slate-1"
        type="submit"
        :disabled="captureBusy"
        :label="$t('BUG_REPORTS.send')"
        :is-loading="busy"
      />
    </fieldset>
  </form>
</template>
